// ADR-011: pure helper for the triage modal's suggested picker tile.
// Port of the Quicken-shaped prototype's `suggestedKind`
// (docs/finance-workflows/quicken-demo-v9.html). No React imports, no hooks;
// lives in src/lib/finance/ next to `searchParamNamespace.ts`. Classify-form
// pre-fill comes only from the bank line's classification hint.

import type { TriageItem } from '@signalvote/shared';

/**
 * The picker tiles the bank-line body (USI-008) can pre-suggest.
 *
 * - `'contribution'`: credit amount → contribution kind
 * - `'expense'`:      debit  amount → expense kind
 * - `'exclude'`:      Exclude from report (ADR-014 — the single non-reporting
 *                     off-ramp that collapsed the retired transfer/dismiss
 *                     suggestions; the heuristic never preempts it today, but
 *                     the value keeps the suggestion↔tile mapping symmetric)
 */
export type SuggestedKind = 'contribution' | 'expense' | 'exclude';

/**
 * Suggested picker tile for a TriageItem. Port of the prototype's
 * `suggestedKind` (docs/finance-workflows/quicken-demo-v9.html).
 *
 * - `bank-line` credit: `'contribution'`.
 * - `bank-line` debit: `'expense'`.
 * - Non-bank-line kinds return `null` (the per-kind body owns its own
 *   pre-fill; the picker is bank-line-only).
 */
export function suggestedKind(item: TriageItem): SuggestedKind | null {
  if (item.kind !== 'BankLine') return null;

  // Credit (positive amount) → contribution; debit (negative) → expense.
  // The Exclude-from-report pick is treasurer-driven; the heuristic does
  // not preempt it.
  return item.bt.amount >= 0 ? 'contribution' : 'expense';
}
